import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Plus, Edit, Trash2, Grid, List, RefreshCw, Key, Folder, ExternalLink, Palette, Download, Link2, FileDown, Eye, EyeOff, Clock, CheckCircle, MoreHorizontal, RotateCcw, Bug, X, Images, Loader2, Layout, Square, Copy, Upload, Maximize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { deleteAsset, createAsset } from '@/utils/collectionsGraphQL';
import { EditCollectionModal } from '@/components/collections/EditCollectionModal';
import { DeleteCollectionDialog } from '@/components/collections/DeleteCollectionDialog';
import { DeleteAssetDialog } from '@/components/collections/DeleteAssetDialog';
import { AssetCard } from '@/components/collections/AssetCard';
import { FavoriteButton } from '@/components/collections/FavoriteButton';
import { AssetViewerWrapper } from '@/components/VisualAssetEditor/AssetViewerWrapper';
import { VisualAssetEditorByIdModal } from '@/components/VisualAssetEditor/VisualAssetEditorByIdModal';
import { AssetTable } from '@/components/collections/AssetTable';
import { CollectionAssetGrid } from '@/components/collections/CollectionAssetGrid';
import { CollectionAssetLightbox } from '@/components/collections/CollectionAssetLightbox';
import { CollectionDataViewer } from '@/components/collections/CollectionDataViewer';
import { ConnectCanvaDesignModal } from '@/components/collections/ConnectCanvaDesignModal';
import { EmbeddedAssetViewer } from '@/components/assets/EmbeddedAssetViewer';
import { CollectionAssetViewer } from '@/components/collections/CollectionAssetViewer';
import { SideBySideEditor } from '@/components/assets/SideBySideEditor';
import CloudinaryViewer from '@/components/collections/CloudinaryViewer';
import { parseCloudinaryUrl, extractCropFromUrl, extractAnnotationFromUrl } from '@/utils/cloudinaryUtils';
import { useAssetById } from '@/hooks/useAssetById';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/utils/edgeFunctionUtils';
import { toast as sonnerToast } from 'sonner';

// Component to toggle between single and side-by-side comparison modes
const ComparisonModeToggle: React.FC<{
  assetId: string;
  onToggle: (assetId: string, mode: 'single' | 'side_by_side') => void;
  refreshKey?: number;
}> = ({ assetId, onToggle, refreshKey }) => {
  const { asset, loading } = useAssetById(assetId);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading...
      </div>
    );
  }

  const currentMode = asset?.comparison_mode || 'side_by_side';

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-600 font-medium">View:</span>
      <ToggleGroup
        type="single"
        value={currentMode}
        onValueChange={(value) => {
          if (value) {
            onToggle(assetId, value as 'single' | 'side_by_side');
          }
        }}
        className="bg-white rounded-md border border-gray-300"
      >
        <ToggleGroupItem
          value="side_by_side"
          className="px-3 py-1.5 text-xs data-[state=on]:bg-blue-100 data-[state=on]:text-blue-700"
          aria-label="Side-by-side view"
        >
          <Layout className="h-3.5 w-3.5 mr-1.5" />
          Side-by-Side
        </ToggleGroupItem>
        <ToggleGroupItem
          value="single"
          className="px-3 py-1.5 text-xs data-[state=on]:bg-blue-100 data-[state=on]:text-blue-700"
          aria-label="Single image view"
        >
          <Square className="h-3.5 w-3.5 mr-1.5" />
          Single
        </ToggleGroupItem>
      </ToggleGroup>
    </div>
  );
};

const CollectionDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [activeTab, setActiveTab] = useState('assets');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isConnectDesignModalOpen, setIsConnectDesignModalOpen] = useState(false);
  const [isEditWebsiteModalOpen, setIsEditWebsiteModalOpen] = useState(false);
  const [websiteStatus, setWebsiteStatus] = useState<'draft' | 'published'>('draft');
  const [publicUrl, setPublicUrl] = useState('');
  const [websiteSlug, setWebsiteSlug] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [isRefetchingAfterSave, setIsRefetchingAfterSave] = useState(false);
  const [debugModalOpen, setDebugModalOpen] = useState(false);
  const [debugAssetData, setDebugAssetData] = useState<any>(null);
  const [lightboxAssetId, setLightboxAssetId] = useState<string | null>(null);
  const [assetRefreshKey, setAssetRefreshKey] = useState(0);
  const [deletingAsset, setDeletingAsset] = useState<any>(null);
  const [sideBySideEditorOpen, setSideBySideEditorOpen] = useState(false);
  const [editorUrls, setEditorUrls] = useState<{ url1: string; url2: string } | null>(null);
  const [currentEditingAssetId, setCurrentEditingAssetId] = useState<string | null>(null);
  const [isUploadingPdf, setIsUploadingPdf] = useState(false);
  const [cloudinaryViewerOpen, setCloudinaryViewerOpen] = useState(false);
  const [cloudinaryViewerData, setCloudinaryViewerData] = useState<{
    imageUrl: string;
    baseUrl: string;
    assetPath: string;
    fullWidth: number;
    fullHeight: number;
    assetId: string;
    initialCrop?: { x: number; y: number; width: number; height: number };
    annotations?: Array<{ x: number; y: number; width: number; height: number }>;
    header?: string;
    subheader?: string;
    isFavorited?: boolean;
  } | null>(null);
  const [currentAssetIndex, setCurrentAssetIndex] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const {
    data: collection,
    isLoading: isLoadingCollection,
    error: collectionError,
    refetch: refetchCollection
  } = useQuery({
    queryKey: ['collection', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('app_collections')
        .select('*')
        .eq('id', id!)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const {
    data: assets = [],
    isLoading: isLoadingAssets,
    error: assetsError,
    refetch: refetchAssets
  } = useQuery({
    queryKey: ['collection-assets', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('app_assets')
        .select(`
          *,
          page:page_id (
            id,
            pricing_page_url,
            company:company_id (
              id,
              slug,
              logo_url
            )
          )
        `)
        .eq('collection_id', id!)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!id,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });


  // Separate query for Canva design data (not available in GraphQL schema yet)
  const { 
    data: canvaDesignData, 
    refetch: refetchCanvaDesign
  } = useQuery({
    queryKey: ['collection-canva-design', id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await supabase
        .from('app_collections')
        .select(`
          canva_design_id, 
          canva_design_title, 
          canva_design_thumbnail, 
          canva_design_view_url, 
          canva_design_edit_url,
          canva_design_details,
          canva_design_public_url,
          website_slug,
          canva_website_pdf_url,
          pdf_last_exported_at,
          pdf_export_settings,
          canva_design_status,
          published_at,
          last_modified_at
        `)
        .eq('id', id)
        .single();
      
      if (error) {
        console.error('Error fetching Canva design data:', error);
        return null;
      }
      
      return data;
    },
    enabled: !!id,
    staleTime: 0, // Always fetch fresh data for PDF updates
    refetchOnWindowFocus: false,
  });

  // Update fields when canvaDesignData becomes available
  React.useEffect(() => {
    if (canvaDesignData?.canva_design_status) {
      setWebsiteStatus(canvaDesignData.canva_design_status);
    }
    if (canvaDesignData?.canva_design_public_url) {
      setPublicUrl(canvaDesignData.canva_design_public_url);
    }
    if (canvaDesignData?.website_slug) {
      setWebsiteSlug(canvaDesignData.website_slug);
    }
  }, [canvaDesignData]);

  // Handle URL parameters for reopening crop editor (legacy support)
  const reopenCropEditor = searchParams.get('reopenCropEditor') === 'true';
  

  const formatDate = (dateString: string) => {
    try {
      // Handle Unix timestamps (both seconds and milliseconds)
      const timestamp = parseInt(dateString);
      if (!isNaN(timestamp)) {
        // If timestamp is less than 1e12, it's likely in seconds, multiply by 1000 for milliseconds
        const date = new Date(timestamp < 1e12 ? timestamp * 1000 : timestamp);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      
      // Handle ISO date strings
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      
      return 'Invalid date';
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return 'Invalid date';
    }
  };

  const formatDateOnly = (dateString: string) => {
    try {
      const timestamp = parseInt(dateString);
      if (!isNaN(timestamp)) {
        const date = new Date(timestamp < 1e12 ? timestamp * 1000 : timestamp);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
      
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
      
      return 'Invalid date';
    } catch (error) {
      return 'Invalid date';
    }
  };

  const formatTimeOnly = (dateString: string) => {
    try {
      const timestamp = parseInt(dateString);
      if (!isNaN(timestamp)) {
        const date = new Date(timestamp < 1e12 ? timestamp * 1000 : timestamp);
        return date.toLocaleTimeString('en-US');
      }
      
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleTimeString('en-US');
      }
      
      return 'Invalid time';
    } catch (error) {
      return 'Invalid time';
    }
  };

  const handleEditSuccess = () => {
    setIsEditModalOpen(false);
    refetchCollection();
    toast({
      title: "Success",
      description: "Collection updated successfully",
    });
  };

  const handleDeleteSuccess = () => {
    toast({
      title: "Success",
      description: "Collection deleted successfully",
    });
    navigate('/collections');
  };



  const handleCanvaReauth = async () => {
    try {
      // Call the existing Canva authorization edge function with shared admin parameter
      const { supabase } = await import('@/integrations/supabase/client');
      const { data, error } = await supabase.functions.invoke('canva-authorize', {
        body: {
          useSharedToken: true // Indicate we want to update the shared admin token
        }
      });

      if (error) {
        console.error('Canva auth error:', error);
        toast({
          title: "Authentication Error",
          description: "Failed to start Canva authentication. Please try again.",
          variant: "destructive",
        });
        return;
      }

      if (data?.auth_url) {
        // Open Canva auth URL in new window
        window.open(data.auth_url, '_blank', 'width=600,height=700');
        
        toast({
          title: "Authentication Started",
          description: "Please complete the authorization to update the shared Canva token.",
        });
      } else {
        toast({
          title: "Error",
          description: "No authentication URL received from server.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Canva auth exception:', error);
      toast({
        title: "Error",
        description: "An error occurred during authentication.",
        variant: "destructive",
      });
    }
  };

  const handleAssetDeleteSuccess = () => {
    refetchAssets();
    toast({
      title: "Success",
      description: "Asset deleted successfully",
    });
  };

  const handleDeleteAssetConfirm = async () => {
    if (!deletingAsset) return;

    try {
      await deleteAsset(deletingAsset.id);
      setDeletingAsset(null);
      handleAssetDeleteSuccess();
    } catch (error) {
      console.error('Error deleting asset:', error);
      toast({
        title: "Error",
        description: "Failed to delete asset",
        variant: "destructive",
      });
    }
  };

  const handleCloneAsset = async (asset: any) => {
    try {
      // Create a copy of the asset with " (Copy)" appended to the title
      const cloneData = {
        collection_id: asset.collection_id,
        type: asset.type,
        page_id: asset.page_id,
        slug: asset.slug,
        version: asset.version,
        title: `${asset.title} (Copy)`,
        description: asset.description,
        primary_image: asset.primary_image,
        secondary_image: asset.secondary_image,
        primary_original_url: asset.primary_original_url,
        primary_cropped_url: asset.primary_cropped_url,
        primary_markup_url: asset.primary_markup_url,
        secondary_original_url: asset.secondary_original_url,
        secondary_cropped_url: asset.secondary_cropped_url,
        secondary_markup_url: asset.secondary_markup_url,
        header: asset.header,
        subheader: asset.subheader,
        comparison_mode: asset.comparison_mode,
        secondary_version: asset.secondary_version,
        secondary_page_id: asset.secondary_page_id,
        primary_annotations: asset.primary_annotations,
        secondary_annotations: asset.secondary_annotations,
      };

      await createAsset(cloneData);

      toast({
        title: "Success",
        description: "Asset cloned successfully",
      });

      refetchAssets();
    } catch (error) {
      console.error('Error cloning asset:', error);
      toast({
        title: "Error",
        description: "Failed to clone asset",
        variant: "destructive",
      });
    }
  };

  const handleAssetUpdateSuccess = () => {
    refetchAssets();
    // No toast message for updates since AssetCard already shows "Asset Updated"
  };

  const handleConnectDesignSuccess = () => {
    refetchCollection();
    refetchCanvaDesign();
    setIsConnectDesignModalOpen(false);
    toast({
      title: "Success",
      description: "Design connected successfully",
    });
  };

  const handlePublishDesign = async () => {
    if (!canvaDesignData?.canva_design_id || !id) return;

    try {
      const { error } = await supabase
        .from('app_collections')
        .update({
          canva_design_status: 'published',
          published_at: new Date().toISOString(),
          last_modified_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) {
        console.error('Error publishing design:', error);
        toast({
          title: "Error",
          description: "Failed to publish design. Please try again.",
          variant: "destructive",
        });
        return;
      }

      refetchCanvaDesign();
      toast({
        title: "Success",
        description: "Design published successfully!",
      });
    } catch (error) {
      console.error('Exception publishing design:', error);
      toast({
        title: "Error",
        description: "An error occurred while publishing the design.",
        variant: "destructive",
      });
    }
  };

  const handleUnpublishDesign = async () => {
    if (!canvaDesignData?.canva_design_id || !id) return;

    try {
      const { error } = await supabase
        .from('app_collections')
        .update({
          canva_design_status: 'draft',
          last_modified_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) {
        console.error('Error unpublishing design:', error);
        toast({
          title: "Error",
          description: "Failed to unpublish design. Please try again.",
          variant: "destructive",
        });
        return;
      }

      refetchCanvaDesign();
      toast({
        title: "Success",
        description: "Design unpublished successfully!",
      });
    } catch (error) {
      console.error('Exception unpublishing design:', error);
      toast({
        title: "Error",
        description: "An error occurred while unpublishing the design.",
        variant: "destructive",
      });
    }
  };

  // Helper functions for image type management (similar to DiffDetail)
  const getEventImageType = (assetId: string) => {
    return eventImageTypes[assetId] || 'cropped';
  };

  const setEventImageType = (assetId: string, imageType: 'markup' | 'cropped' | 'original') => {
    setEventImageTypes(prev => ({ ...prev, [assetId]: imageType }));
  };

  // Handler to toggle comparison mode
  const handleToggleComparisonMode = async (assetId: string, newMode: 'single' | 'side_by_side') => {
    try {
      // Use direct database update instead of GraphQL
      const { error } = await supabase
        .from('app_assets')
        .update({
          comparison_mode: newMode,
          updated_at: new Date().toISOString(),
        })
        .eq('id', assetId);

      if (error) throw error;

      toast({
        title: "Success",
        description: `View mode updated to ${newMode === 'single' ? 'single image' : 'side-by-side'}`,
      });
      // Force a refresh of asset components
      setAssetRefreshKey(prev => prev + 1);
      refetchAssets();
    } catch (error) {
      console.error('Error updating comparison mode:', error);
      toast({
        title: "Error",
        description: "Failed to update view mode",
        variant: "destructive",
      });
    }
  };

  // Handler to open side-by-side editor
  const handleOpenSideBySideEditor = async (assetId: string) => {
    if (!assetId) {
      sonnerToast.error('No asset found');
      return;
    }

    // Get the current imageType being displayed
    const imageType = getEventImageType(assetId);

    try {
      // Fetch the asset using direct database query
      const { data: asset, error } = await supabase
        .from('app_assets')
        .select('id, slug, primary_cropped_url, primary_markup_url, primary_original_url, primary_image, secondary_cropped_url, secondary_markup_url, secondary_original_url, secondary_image, version, secondary_version, comparison_mode')
        .eq('id', assetId)
        .single();

      if (error) throw error;

      if (!asset) {
        sonnerToast.error('Asset not found');
        return;
      }

      const comparisonMode = asset.comparison_mode || 'side_by_side';

      // Helper function to get URL based on image type
      const getUrl = (prefix: 'primary' | 'secondary') => {
        switch (imageType) {
          case 'markup':
            return asset[`${prefix}_markup_url`] || asset[`${prefix}_cropped_url`] || asset[`${prefix}_original_url`] || asset[`${prefix}_image`];
          case 'cropped':
            return asset[`${prefix}_cropped_url`] || asset[`${prefix}_original_url`] || asset[`${prefix}_image`];
          case 'original':
            return asset[`${prefix}_original_url`] || asset[`${prefix}_image`];
          default:
            return asset[`${prefix}_cropped_url`] || asset[`${prefix}_original_url`] || asset[`${prefix}_image`];
        }
      };

      if (comparisonMode === 'single') {
        // Single mode: edit whichever image exists (prefer secondary, fallback to primary)
        const url2 = getUrl('secondary') || getUrl('primary');

        if (!url2) {
          sonnerToast.error('No image available for editing');
          return;
        }

        // Open editor with only the image (pass same URL for both to maintain compatibility)
        setCurrentEditingAssetId(assetId);
        setEditorUrls({ url1: url2, url2 });
        setSideBySideEditorOpen(true);
      } else {
        // Side-by-side mode: edit both images
        let url1 = asset.primary_cropped_url || getUrl('primary');
        let url2 = asset.secondary_cropped_url || getUrl('secondary');

        if (!url1 || !url2) {
          sonnerToast.error('Both images are required for side-by-side editing');
          return;
        }

        // Open editor with both images
        setCurrentEditingAssetId(assetId);
        setEditorUrls({ url1, url2 });
        setSideBySideEditorOpen(true);
      }
    } catch (error) {
      console.error('Error fetching asset:', error);
      sonnerToast.error('Failed to load asset images');
    }
  };

  // Handler to save edited images from side-by-side editor
  const handleSaveSideBySideImages = async (url1: string, url2: string) => {
    if (!currentEditingAssetId) {
      sonnerToast.error('No asset selected for editing');
      return;
    }

    try {
      // Fetch current asset to check comparison mode using direct database query
      const { data: asset, error: fetchError } = await supabase
        .from('app_assets')
        .select('id, comparison_mode')
        .eq('id', currentEditingAssetId)
        .single();

      if (fetchError) throw fetchError;

      if (!asset) {
        sonnerToast.error('Asset not found');
        return;
      }

      // In single mode, url1 and url2 are the same
      // Update both to ensure consistency
      const updateData = asset.comparison_mode === 'single'
        ? {
            primary_cropped_url: url2,
            secondary_cropped_url: url2,
            updated_at: new Date().toISOString(),
          }
        : {
            primary_cropped_url: url1,
            secondary_cropped_url: url2,
            updated_at: new Date().toISOString(),
          };

      // Use direct database update instead of GraphQL
      const { error: updateError } = await supabase
        .from('app_assets')
        .update(updateData)
        .eq('id', currentEditingAssetId);

      if (updateError) throw updateError;

      sonnerToast.success('Images updated successfully');

      // Close editor and clear state
      setSideBySideEditorOpen(false);
      setEditorUrls(null);
      setCurrentEditingAssetId(null);

      // Force a refresh of the asset viewer
      setAssetRefreshKey(prev => prev + 1);

      // Refetch the asset data
      refetchAssets();
    } catch (error) {
      console.error('Error saving images:', error);
      sonnerToast.error('Failed to save images');
    }
  };

  // Handler to cancel side-by-side editor
  const handleCancelSideBySideEditor = () => {
    setSideBySideEditorOpen(false);
    setEditorUrls(null);
    setCurrentEditingAssetId(null);
  };

  // Handler to open Cloudinary viewer
  const handleOpenCloudinaryViewer = async (assetId: string) => {
    // Find the index of this asset in the assets array
    const assetIndex = assets.findIndex((a: any) => a.id === assetId);
    if (assetIndex !== -1) {
      setCurrentAssetIndex(assetIndex);
    }

    try {
      // Fetch the asset data directly from database
      const { data: asset, error } = await supabase
        .from('app_assets')
        .select('id, primary_cropped_url, primary_original_url, primary_image, header, subheader, is_favorited, primary_annotations')
        .eq('id', assetId)
        .single();

      if (error) throw error;

      if (!asset) {
        sonnerToast.error('Asset not found');
        return;
      }

      // Get the image URL (prefer cropped, fallback to original or primary_image)
      const imageUrl = asset.primary_cropped_url || asset.primary_original_url || asset.primary_image;

      console.log('Opening CloudinaryViewer with imageUrl from database:', imageUrl);

      if (!imageUrl) {
        sonnerToast.error('No image available for this asset');
        return;
      }

      // Parse the Cloudinary URL
      const parsed = parseCloudinaryUrl(imageUrl);

      if (!parsed) {
        sonnerToast.error('Invalid Cloudinary URL - This feature only works with Cloudinary-hosted images');
        return;
      }

      // Extract the current crop from the URL (if it exists)
      const currentCrop = extractCropFromUrl(imageUrl);

      // Use annotations from database if available, otherwise extract from URL
      let annotations = asset.primary_annotations || [];

      // If no annotations in database, try to extract from URL (for backwards compatibility)
      if (!annotations || annotations.length === 0) {
        const annotation = extractAnnotationFromUrl(imageUrl, currentCrop || undefined);
        annotations = annotation ? [annotation] : [];
      }

      // For now, use default dimensions (these should ideally come from the asset metadata)
      // You can enhance this by storing image dimensions in the database
      setCloudinaryViewerData({
        imageUrl, // Pass the exact database URL
        baseUrl: parsed.baseUrl,
        assetPath: parsed.assetPath,
        fullWidth: 1440, // Default width - TODO: get from asset metadata
        fullHeight: 3000, // Default height - TODO: get from asset metadata
        assetId,
        initialCrop: currentCrop || undefined,
        annotations: annotations.length > 0 ? annotations : undefined,
        header: asset.header || '',
        subheader: asset.subheader || '',
        isFavorited: asset.is_favorited || false,
      });
      setCloudinaryViewerOpen(true);
    } catch (error) {
      console.error('Error opening Cloudinary viewer:', error);
      sonnerToast.error('Failed to open viewer');
    }
  };

  // Navigate to previous asset in viewer
  const handlePreviousAsset = () => {
    if (currentAssetIndex > 0) {
      const prevAsset = assets[currentAssetIndex - 1];
      if (prevAsset?.id) {
        handleOpenCloudinaryViewer(prevAsset.id);
      }
    }
  };

  // Navigate to next asset in viewer
  const handleNextAsset = () => {
    if (currentAssetIndex < assets.length - 1) {
      const nextAsset = assets[currentAssetIndex + 1];
      if (nextAsset?.id) {
        handleOpenCloudinaryViewer(nextAsset.id);
      }
    }
  };

  // Handler to save asset metadata (header and subheader)
  const handleSaveAssetMetadata = async (header: string, subheader: string) => {
    if (!cloudinaryViewerData?.assetId) {
      sonnerToast.error('No asset selected');
      return;
    }

    try {
      // Use direct database update instead of GraphQL
      const { error } = await supabase
        .from('app_assets')
        .update({
          header,
          subheader,
          updated_at: new Date().toISOString(),
        })
        .eq('id', cloudinaryViewerData.assetId);

      if (error) throw error;

      // Update the local state
      setCloudinaryViewerData({
        ...cloudinaryViewerData,
        header,
        subheader,
      });

      // Refresh assets
      setAssetRefreshKey(prev => prev + 1);
      refetchAssets();
    } catch (error) {
      console.error('Error saving metadata:', error);
      throw error; // Re-throw so the component can show error toast
    }
  };

  // Handler to save cropped image from Cloudinary viewer
  const handleSaveCloudinaryCrop = async (cropUrl: string, crop: any, annotations?: Array<{ x: number; y: number; width: number; height: number }>) => {
    if (!cloudinaryViewerData?.assetId) {
      sonnerToast.error('No asset selected');
      return;
    }

    try {
      // Use direct database update instead of GraphQL
      const { error } = await supabase
        .from('app_assets')
        .update({
          primary_cropped_url: cropUrl,
          primary_annotations: annotations || [],
          updated_at: new Date().toISOString(),
        })
        .eq('id', cloudinaryViewerData.assetId);

      if (error) throw error;

      // Reload the asset from database to get the fresh saved URL
      const { data: updatedAsset, error: fetchError } = await supabase
        .from('app_assets')
        .select('primary_cropped_url, primary_annotations')
        .eq('id', cloudinaryViewerData.assetId)
        .single();

      if (fetchError) throw fetchError;

      // Parse the saved URL to extract crop (create new object)
      const savedCrop = extractCropFromUrl(updatedAsset.primary_cropped_url || cropUrl);

      // Update the viewer data with the exact database URL
      // IMPORTANT: Update imageUrl so the viewer shows the exact saved image
      setCloudinaryViewerData({
        ...cloudinaryViewerData,
        imageUrl: updatedAsset.primary_cropped_url || cropUrl, // Use exact URL from database
        initialCrop: savedCrop ? { ...savedCrop } : cloudinaryViewerData.initialCrop,
        annotations: updatedAsset.primary_annotations ? [...updatedAsset.primary_annotations] : [],
      });

      // Refresh assets in background
      setAssetRefreshKey(prev => prev + 1);
      refetchAssets();
    } catch (error) {
      console.error('Error saving crop:', error);
      sonnerToast.error('Failed to save crop');
    }
  };

  const handleUploadPdf = async (file: File) => {
    if (!id) {
      toast({
        title: "Error",
        description: "No collection ID found",
        variant: "destructive",
      });
      return;
    }

    if (file.type !== 'application/pdf') {
      toast({
        title: "Invalid File",
        description: "Please select a PDF file",
        variant: "destructive",
      });
      return;
    }

    setIsUploadingPdf(true);
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session) {
        console.error('Authentication error:', sessionError);
        toast({
          title: "Authentication Error",
          description: "Please ensure you're logged in and try again.",
          variant: "destructive",
        });
        setIsUploadingPdf(false);
        return;
      }

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('collectionId', id);

      // Call the upload-pdf edge function
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(
        `${supabaseUrl}/functions/v1/upload-pdf`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: formData,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Upload failed');
      }

      if (data.success) {
        toast({
          title: "PDF Uploaded Successfully",
          description: "The PDF has been uploaded and is now available for viewing.",
        });

        // Refresh the Canva design data to show the new PDF
        await refetchCanvaDesign();
      } else {
        throw new Error(data.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Upload Failed",
        description: error instanceof Error ? error.message : "An error occurred during upload.",
        variant: "destructive",
      });
    } finally {
      setIsUploadingPdf(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUploadPdf(file);
    }
  };

  const handleExportDesign = async (format: 'pdf' | 'png' | 'jpg' = 'pdf') => {
    if (!canvaDesignData?.canva_design_id) {
      toast({
        title: "Error",
        description: "No design connected to export",
        variant: "destructive",
      });
      return;
    }

    setIsExporting(true);
    try {
      // Get user session for authentication (similar to connect design)
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        console.error('Authentication error:', sessionError);
        toast({
          title: "Authentication Error",
          description: "Please ensure you're logged in and try again.",
          variant: "destructive",
        });
        setIsExporting(false);
        return;
      }

      // Use invokeEdgeFunction with explicit auth header
      const { data, error } = await invokeEdgeFunction('canva-export-design', {
        designId: canvaDesignData.canva_design_id,
        format,
        quality: 'high'
      }, {
        'Authorization': `Bearer ${session.access_token}`
      });

      if (error) {
        console.error('Export error:', error);
        toast({
          title: "Export Failed",
          description: `Failed to export design: ${error.message || error}`,
          variant: "destructive",
        });
        return;
      }

      if (data?.success) {
        const downloadUrl = data.storage_url || data.download_url;
        const filename = data.filename || `${canvaDesignData.canva_design_title || 'design'}.${format}`;
        
        if (downloadUrl) {
          // If this is a PDF export, save the URL for website access
          if (format === 'pdf' && data.storage_url && id) {
            try {
              const { error: updateError } = await supabase
                .from('app_collections')
                .update({
                  canva_website_pdf_url: data.storage_url,
                  pdf_last_exported_at: new Date().toISOString(),
                  pdf_export_settings: {
                    format,
                    quality: 'high',
                    filename,
                    exported_at: new Date().toISOString()
                  },
                  last_modified_at: new Date().toISOString(),
                })
                .eq('id', id);

              if (updateError) {
                console.error('Database update error:', updateError);
              } else {
                // Refresh the Canva design data to show the new PDF
                await refetchCanvaDesign();
              }
            } catch (error) {
              console.error('Error saving PDF URL:', error);
              // Don't fail the export if PDF URL saving fails
            }
          }

          toast({
            title: "Report Generated Successfully",
            description: format === 'pdf' && data.storage_url
              ? "PDF report has been generated and is now available for viewing."
              : `Design exported as ${format.toUpperCase()}.`,
          });
        } else {
          toast({
            title: "Export Failed",
            description: "No download URL available",
            variant: "destructive",
          });
        }
      } else {
        toast({
          title: "Export Failed",
          description: data?.error || "Unknown error occurred during export",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Export exception:', error);
      toast({
        title: "Export Failed",
        description: "An error occurred during export.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  if (collectionError || assetsError) {
    const error = collectionError || assetsError;
    return (
      <div className="min-h-screen from-purple-50 to-blue-50">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-8">
            <div className="text-center">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Error loading collection</h2>
              <p className="text-gray-600 mb-4">{error instanceof Error ? error.message : 'Unknown error'}</p>
              <div className="space-x-4">
                <Button variant="outline" onClick={() => navigate('/collections')}>
                  Back to Collections
                </Button>
                <Button onClick={() => {
                  refetchCollection();
                  refetchAssets();
                }}>
                  Try Again
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoadingCollection) {
    return (
      <div className="min-h-screen from-purple-50 to-blue-50">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-8">
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600">Loading collection...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!collection) {
    return (
      <div className="min-h-screen from-purple-50 to-blue-50">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-8">
            <div className="text-center">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Collection not found</h2>
              <p className="text-gray-600 mb-4">The collection you're looking for doesn't exist.</p>
              <Button onClick={() => navigate('/collections')}>
                Back to Collections
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen from-purple-50 to-blue-50">
      {/* Hidden file input for PDF upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        onChange={handleFileInputChange}
        className="hidden"
      />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-8">
          {/* Back Navigation */}
          <div className="mb-6">
            <Button variant="ghost" asChild className="mb-4">
              <Link to="/collections" className="flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                Back to Collections
              </Link>
            </Button>
          </div>

          {/* Collection Header */}
          <div className="mb-8">
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <CardTitle className="text-2xl">{collection.name}</CardTitle>
                    {collection.description && (
                      <CardDescription className="text-base">
                        {collection.description}
                      </CardDescription>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {collection.canva_url && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(collection.canva_url, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Open in Canva
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsEditModalOpen(true)}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsDeleteDialogOpen(true)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </div>
              </CardHeader>
              {/* Canva Folder Information - only show if present */}
              {collection.canva_folder_name && (
                <CardContent>
                  <div className="flex items-center gap-2">
                    <Folder className="h-4 w-4 text-purple-600" />
                    <span className="text-sm font-medium text-gray-900">Canva Folder:</span>
                    {collection.canva_folder_url ? (
                      <a 
                        href={collection.canva_folder_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-purple-600 hover:text-purple-800 hover:underline flex items-center gap-1"
                      >
                        {collection.canva_folder_name}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : (
                      <span className="text-sm text-gray-600">{collection.canva_folder_name}</span>
                    )}
                    <a 
                      href="https://www.canva.com/design/DAGxkP2ypfo/_b1OQbQPOQLYaaLOAh4ORQ/view?utm_content=DAGxkP2ypfo&utm_campaign=designshare&utm_medium=link&utm_source=publishsharelink&mode=preview"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1 ml-4"
                    >
                      <Plus className="h-3 w-3" />
                      Swipe File
                    </a>
                  </div>
                </CardContent>
              )}
            </Card>
          </div>

          {/* Content Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="assets" className="flex items-center gap-2">
                <Grid className="w-4 h-4" />
                Assets
              </TabsTrigger>
              <TabsTrigger value="canva-designs" className="flex items-center gap-2">
                <Palette className="w-4 h-4" />
                Resources
              </TabsTrigger>
            </TabsList>

            <TabsContent value="assets" className="space-y-6">
              {/* Assets Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Assets</h2>
                  <p className="text-gray-600 text-sm">
                    {assets.length} {assets.length === 1 ? 'asset' : 'assets'} in this collection
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Button
                      variant={viewMode === 'grid' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setViewMode('grid')}
                    >
                      <Grid className="w-4 h-4" />
                    </Button>
                    <Button
                      variant={viewMode === 'table' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setViewMode('table')}
                    >
                      <List className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Assets Content */}
              {isLoadingAssets ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading assets...</p>
                  </div>
                </div>
              ) : assets.length === 0 ? (
                <div className="text-center py-12">
                  <div className="max-w-sm mx-auto">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                      <Grid className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No assets yet</h3>
                    <p className="text-gray-600 mb-4">
                      Add your first asset to this collection.
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <CollectionAssetGrid
                    assets={assets}
                    viewMode={viewMode}
                    onOpenLightbox={setLightboxAssetId}
                    onRefresh={refetchAssets}
                  />
                </>
              )}
            </TabsContent>

            <TabsContent value="canva-designs" className="space-y-6">
              {/* Resources Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Resources</h2>
                  <p className="text-gray-600 text-sm">
                    Connect and manage designs from your Canva account
                  </p>
                </div>
              </div>

              {/* Website Section */}
              <Card className="w-fit hover:shadow-md transition-shadow">
                <CardContent className="p-0">
                  {canvaDesignData?.canva_design_id ? (
                    <div className="p-4 w-96">
                      {/* Header with Badges */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="bg-blue-100 text-blue-800 px-2 py-1 text-xs">
                            Website
                          </Badge>
                        </div>
                        <Badge 
                          variant="secondary" 
                          className={`px-2 py-1 text-xs flex items-center gap-1 ${
                            canvaDesignData?.canva_design_status === 'published' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {canvaDesignData?.canva_design_status === 'published' ? (
                            <>
                              <CheckCircle className="w-3 h-3" />
                              Published
                            </>
                          ) : (
                            <>
                              <Clock className="w-3 h-3" />
                              Draft
                            </>
                          )}
                        </Badge>
                      </div>
                      
                      {/* Design Preview */}
                      <div 
                        className="mb-4 cursor-pointer group"
                        onClick={() => setIsEditWebsiteModalOpen(true)}
                        title="Click to edit website settings"
                      >
                        {canvaDesignData.canva_design_thumbnail ? (
                          <img
                            src={canvaDesignData.canva_design_thumbnail}
                            alt={canvaDesignData.canva_design_title || 'Website Design'}
                            className="w-full h-32 object-contain rounded-lg border bg-white group-hover:border-blue-300 transition-colors"
                          />
                        ) : (
                          <div className="w-full h-32 bg-gray-100 rounded-lg border flex items-center justify-center group-hover:border-blue-300 group-hover:bg-gray-50 transition-colors">
                            <Palette className="w-8 h-8 text-gray-400 group-hover:text-gray-500" />
                          </div>
                        )}
                      </div>
                      
                      {/* Title and Description */}
                      <div 
                        className="space-y-2 mb-4 cursor-pointer group"
                        onClick={() => setIsEditWebsiteModalOpen(true)}
                        title="Click to edit website settings"
                      >
                        <h3 className="text-lg font-semibold text-gray-900 line-clamp-2 group-hover:text-blue-600 transition-colors">
                          {collection.name}
                        </h3>
                        <p className="text-sm text-gray-600 line-clamp-2 group-hover:text-gray-800 transition-colors">
                          {collection.description || 'No description available'}
                        </p>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                        <div className="flex items-center gap-2">
                          <button 
                            onClick={() => {
                              if (canvaDesignData.canva_website_pdf_url) {
                                window.open(canvaDesignData.canva_website_pdf_url, '_blank');
                              }
                            }}
                            disabled={!canvaDesignData.canva_website_pdf_url}
                            className={`p-2 rounded-lg transition-colors flex items-center gap-2 text-sm ${
                              canvaDesignData.canva_website_pdf_url 
                                ? 'hover:bg-gray-100 text-gray-600' 
                                : 'text-gray-400 cursor-not-allowed'
                            }`}
                            title={canvaDesignData.canva_website_pdf_url ? "Download PDF" : "PDF not available"}
                          >
                            <Download className="w-4 h-4" />
                            Download
                          </button>
                        </div>
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button 
                              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                              title="More Options"
                            >
                              <MoreHorizontal className="w-4 h-4 text-gray-600" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-96">
                            {/* Current PDF URL Display */}
                            {canvaDesignData.canva_website_pdf_url && (
                              <div className="px-2 py-2 mb-2 border-b">
                                <div className="text-xs font-medium text-gray-500 mb-1">Current PDF:</div>
                                <div className="text-xs text-gray-700 break-all font-mono bg-gray-50 p-2 rounded">
                                  {canvaDesignData.canva_website_pdf_url}
                                </div>
                              </div>
                            )}

                            {canvaDesignData.canva_design_edit_url && (
                              <DropdownMenuItem onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                window.open(canvaDesignData.canva_design_edit_url, '_blank');
                              }}>
                                <Palette className="w-4 h-4 mr-2" />
                                Edit in Canva
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleExportDesign('pdf');
                              }}
                              disabled={isExporting}
                            >
                              {isExporting ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Updating PDF...
                                </>
                              ) : (
                                <>
                                  <RotateCcw className="w-4 h-4 mr-2" />
                                  Update PDF from Canva
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                fileInputRef.current?.click();
                              }}
                              disabled={isUploadingPdf}
                            >
                              {isUploadingPdf ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Uploading PDF...
                                </>
                              ) : (
                                <>
                                  <Upload className="w-4 h-4 mr-2" />
                                  Upload PDF from Computer
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setIsConnectDesignModalOpen(true);
                            }}>
                              <Link2 className="w-4 h-4 mr-2" />
                              Swap Canva Design
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ) : (
                    <div className="p-6">
                      <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                        <div className="w-16 h-16 mx-auto mb-4 bg-purple-100 rounded-lg flex items-center justify-center">
                          <Palette className="w-8 h-8 text-purple-600" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Website</h3>
                        <p className="text-gray-600 mb-6">Connect your Canva design</p>
                        <Button
                          onClick={() => setIsConnectDesignModalOpen(true)}
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          <Link2 className="w-4 h-4 mr-2" />
                          Connect Website Design
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Future sections can be added here */}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxAssetId && (
        <CollectionAssetLightbox
          assetId={lightboxAssetId}
          assets={assets}
          onClose={() => setLightboxAssetId(null)}
          onAssetUpdate={refetchAssets}
          onOpenCloudinaryViewer={(assetId) => {
            setLightboxAssetId(null);
            handleOpenCloudinaryViewer(assetId);
          }}
          onOpenSideBySideEditor={(assetId) => {
            setLightboxAssetId(null);
            handleOpenSideBySideEditor(assetId);
          }}
        />
      )}

      {/* Modals */}
      <EditCollectionModal
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        collection={collection}
        onSuccess={handleEditSuccess}
      />

      <DeleteCollectionDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        collection={collection}
        onConfirm={handleDeleteSuccess}
      />

      <ConnectCanvaDesignModal
        open={isConnectDesignModalOpen}
        onOpenChange={setIsConnectDesignModalOpen}
        collectionId={id!}
        onSuccess={handleConnectDesignSuccess}
      />

      {/* Edit Website Status Modal */}
      <Dialog open={isEditWebsiteModalOpen} onOpenChange={setIsEditWebsiteModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Website Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={websiteStatus} onValueChange={(value: 'draft' | 'published') => setWebsiteStatus(value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Draft
                    </div>
                  </SelectItem>
                  <SelectItem value="published">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Published
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="publicUrl">Public URL</Label>
              <Input
                id="publicUrl"
                value={publicUrl}
                onChange={(e) => setPublicUrl(e.target.value)}
                placeholder="https://example.com/website"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="websiteSlug">Website Slug</Label>
              <Input
                id="websiteSlug"
                value={websiteSlug}
                onChange={(e) => setWebsiteSlug(e.target.value)}
                placeholder="my-website-slug"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditWebsiteModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={async () => {
              try {
                // Save status to collection in database
                const { error } = await supabase
                  .from('app_collections')
                  .update({ 
                    canva_design_status: websiteStatus,
                    published_at: websiteStatus === 'published' ? new Date().toISOString() : null,
                    canva_design_public_url: publicUrl || null,
                    website_slug: websiteSlug || null
                  })
                  .eq('id', id);

                if (error) throw error;

                // Refetch the canva design data to update the card
                refetchCanvaDesign();

                toast({
                  title: "Status updated",
                  description: `Website status changed to ${websiteStatus}`,
                });
                setIsEditWebsiteModalOpen(false);
              } catch (error) {
                console.error('Error updating status:', error);
                toast({
                  title: "Error",
                  description: "Failed to update website status",
                  variant: "destructive",
                });
              }
            }}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Asset Debug Modal */}
      {debugModalOpen && debugAssetData && (
        <Dialog open={debugModalOpen} onOpenChange={setDebugModalOpen}>
          <DialogContent className="sm:max-w-[80vw] max-h-[80vh] overflow-auto">
            <DialogHeader>
              <DialogTitle>Asset Debug Data - {debugAssetData.id}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Quick Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-semibold text-sm">Asset ID</h4>
                  <p className="text-xs font-mono">{debugAssetData.id}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-sm">Comparison Mode</h4>
                  <p className="text-xs">{debugAssetData.comparison_mode || 'single'}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-sm">Header</h4>
                  <p className="text-xs">{debugAssetData.header || '(none)'}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-sm">Subheader</h4>
                  <p className="text-xs">{debugAssetData.subheader || '(none)'}</p>
                </div>
              </div>

              {/* Image URLs */}
              <div className="space-y-3">
                <h4 className="font-semibold">Image URLs</h4>
                <div className="grid grid-cols-1 gap-3">
                  <div className="p-3 bg-blue-50 rounded border">
                    <h5 className="font-medium text-sm text-blue-800">Primary Images</h5>
                    <div className="mt-2 space-y-1 text-xs">
                      <div><strong>Original:</strong> {debugAssetData.primary_original_url || '(none)'}</div>
                      <div><strong>Cropped:</strong> {debugAssetData.primary_cropped_url || '(none)'}</div>
                      <div><strong>Legacy:</strong> {debugAssetData.primary_image || '(none)'}</div>
                    </div>
                  </div>
                  <div className="p-3 bg-green-50 rounded border">
                    <h5 className="font-medium text-sm text-green-800">Secondary Images</h5>
                    <div className="mt-2 space-y-1 text-xs">
                      <div><strong>Original:</strong> {debugAssetData.secondary_original_url || '(none)'}</div>
                      <div><strong>Cropped:</strong> {debugAssetData.secondary_cropped_url || '(none)'}</div>
                      <div><strong>Version:</strong> {debugAssetData.secondary_version || '(none)'}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Viewport States */}
              <div className="space-y-3">
                <h4 className="font-semibold">Viewport States</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="p-3 bg-purple-50 rounded border">
                    <h5 className="font-medium text-sm text-purple-800">Primary Viewport State</h5>
                    <pre className="mt-2 text-xs overflow-x-auto bg-white p-2 rounded border">
                      {JSON.stringify(debugAssetData.primary_viewport_state, null, 2) || 'null'}
                    </pre>
                  </div>
                  <div className="p-3 bg-orange-50 rounded border">
                    <h5 className="font-medium text-sm text-orange-800">Secondary Viewport State</h5>
                    <pre className="mt-2 text-xs overflow-x-auto bg-white p-2 rounded border">
                      {JSON.stringify(debugAssetData.secondary_viewport_state, null, 2) || 'null'}
                    </pre>
                  </div>
                </div>
              </div>

              {/* Full Asset Data */}
              <div className="space-y-3">
                <h4 className="font-semibold">Complete Asset Data</h4>
                <div className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-auto max-h-64">
                  <pre className="text-xs whitespace-pre-wrap">
                    {JSON.stringify(debugAssetData, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => setDebugModalOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Debug viewer for collection data stored in localStorage */}
      <CollectionDataViewer />

      {/* Delete Asset Dialog */}
      {deletingAsset && (
        <DeleteAssetDialog
          open={!!deletingAsset}
          onOpenChange={(open) => !open && setDeletingAsset(null)}
          asset={deletingAsset}
          onConfirm={handleDeleteAssetConfirm}
        />
      )}

      {/* Cloudinary Viewer Dialog */}
      {cloudinaryViewerOpen && cloudinaryViewerData && (
        <Dialog open={cloudinaryViewerOpen} onOpenChange={(open) => {
          if (!open) {
            setCloudinaryViewerOpen(false);
            setCloudinaryViewerData(null);
          }
        }}>
          <DialogContent className="max-w-none w-screen h-screen p-0 m-0 border-0 [&>button]:hidden">
            <CloudinaryViewer
              imageUrl={cloudinaryViewerData.imageUrl}
              baseUrl={cloudinaryViewerData.baseUrl}
              assetPath={cloudinaryViewerData.assetPath}
              fullWidth={cloudinaryViewerData.fullWidth}
              fullHeight={cloudinaryViewerData.fullHeight}
              initialCrop={cloudinaryViewerData.initialCrop}
              annotations={cloudinaryViewerData.annotations}
              onClose={() => {
                setCloudinaryViewerOpen(false);
                setCloudinaryViewerData(null);
              }}
              onSave={handleSaveCloudinaryCrop}
              currentIndex={currentAssetIndex}
              totalAssets={assets.length}
              onPrevious={handlePreviousAsset}
              onNext={handleNextAsset}
              initialHeader={cloudinaryViewerData.header}
              initialSubheader={cloudinaryViewerData.subheader}
              onSaveMetadata={handleSaveAssetMetadata}
              assetId={cloudinaryViewerData.assetId}
              isFavorited={cloudinaryViewerData.isFavorited}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Side-by-Side Editor Dialog */}
      {sideBySideEditorOpen && editorUrls && currentEditingAssetId && (
        <Dialog open={sideBySideEditorOpen} onOpenChange={(open) => {
          if (!open) handleCancelSideBySideEditor();
        }}>
          <DialogContent className="max-w-none w-screen h-screen p-0 m-0 border-0 [&>button]:hidden">
            <SideBySideEditor
              leftImageUrl={editorUrls.url1}
              rightImageUrl={editorUrls.url2}
              onSave={handleSaveSideBySideEditor}
              onCancel={handleCancelSideBySideEditor}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Footer */}
      <div className="border-t border-gray-200 mt-12 pt-8">
        <div className="flex flex-col items-center gap-2">
          <button
            onClick={handleCanvaReauth}
            className="text-sm text-gray-500 hover:text-purple-600 transition-colors flex items-center gap-1"
          >
            <Key className="w-3 h-3" />
            Update Shared Canva Token
          </button>
          <p className="text-xs text-gray-400">
            This will authenticate with Canva and store the token for all users to share
          </p>
        </div>
      </div>

    </div>
  );
};

export default CollectionDetail;